var _effectorFileName = "/builds/gmini/front-ui-kit/src/ResizableColumns/ResizableColumns.tsx";
import React, { Children } from 'react';
import { AutoSizer } from 'react-virtualized';
import { Panel } from './Panel';
import { Resizer } from './Resizer';
import { PanelRoot } from './ResizableColumns.styled';
import { convertPcToPx } from './utils/convert';
import { arrEqual } from './utils/equal';
import { canFoldColumn, expandColumn, foldColumn, isExpanded } from './utils/expand';
import { calcAllowedDelta, changeWidth } from './utils/resize';
import { getFromStorage, saveToStorage } from './utils/persistent';
import { correctWidths } from './utils/correct-widths';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const Resizable = /*#__PURE__*/React.forwardRef(({
  children,
  foldedPanelWidthPx,
  storage = localStorage,
  storageKey,
  onWidthsChanged,
  onWidthChangedDelay = 100
}, ref) => {
  const initializedRef = React.useRef({
    initialized: false
  });
  const columnsJsxElements = Children.toArray(children);
  const [widthsPc, setWidthsPc] = React.useState(() => columnsJsxElements.map(() => 100 / columnsJsxElements.length));
  const dragInfoRef = React.useRef({
    dragInfo: null
  });
  const refContainerWidth = React.useRef({
    widthPx: 0
  });
  const [isDragging, setIsDragging] = React.useState(false);

  const onMouseDown = (e, idx) => {
    dragInfoRef.current.dragInfo = {
      activePanelIdx: idx,
      separatorPosition: e.clientX
    };
    setIsDragging(true);
  };

  const onExpandColumn = idx => {
    const containerW = refContainerWidth.current.widthPx;
    setWidthsPc(expandColumn({
      containerW,
      foldedWidthPx: foldedPanelWidthPx,
      idx,
      widthsPc
    }));
    const elem = columnsJsxElements[idx];

    if ( /*#__PURE__*/React.isValidElement(elem)) {
      var _elem$props$onOpened, _elem$props;

      (_elem$props$onOpened = (_elem$props = elem.props).onOpened) === null || _elem$props$onOpened === void 0 ? void 0 : _elem$props$onOpened.call(_elem$props);
    }
  };

  const onFoldColumn = idx => {
    const containerW = refContainerWidth.current.widthPx;

    if (!canFoldColumn({
      containerW,
      widthsPc,
      foldedWidthPx: foldedPanelWidthPx
    })) {
      return;
    }

    setWidthsPc(foldColumn({
      containerW,
      foldedWidthPx: foldedPanelWidthPx,
      idx,
      widthsPc
    }));
    const elem = columnsJsxElements[idx];

    if ( /*#__PURE__*/React.isValidElement(elem)) {
      var _elem$props$onClosed, _elem$props2;

      (_elem$props$onClosed = (_elem$props2 = elem.props).onClosed) === null || _elem$props$onClosed === void 0 ? void 0 : _elem$props$onClosed.call(_elem$props2);
    }
  };

  const onChangeContainerWidth = nextWidth => {
    setWidthsPc(prev => correctWidths({
      containerW: nextWidth,
      prevContainerW: refContainerWidth.current.widthPx || nextWidth,
      foldedWidthPx: foldedPanelWidthPx,
      widthsPc: prev
    }));
    refContainerWidth.current.widthPx = nextWidth;
  };

  if (ref) {
    const api = {
      openPanel: idx => {
        const containerW = refContainerWidth.current.widthPx;

        if (!isExpanded({
          containerW,
          foldedWidthPx: foldedPanelWidthPx,
          widthPc: widthsPc[idx]
        })) {
          setWidthsPc(expandColumn({
            containerW,
            foldedWidthPx: foldedPanelWidthPx,
            idx,
            widthsPc
          }));
        }
      },
      closePanel: idx => {
        const containerW = refContainerWidth.current.widthPx;

        if (isExpanded({
          containerW,
          foldedWidthPx: foldedPanelWidthPx,
          widthPc: widthsPc[idx]
        }) && canFoldColumn({
          containerW,
          foldedWidthPx: foldedPanelWidthPx,
          widthsPc
        })) {
          setWidthsPc(foldColumn({
            containerW,
            foldedWidthPx: foldedPanelWidthPx,
            idx,
            widthsPc
          }));
        }
      }
    };

    if (typeof ref === 'function') {
      ref(api);
    } else {
      ref.current = api;
    }
  }

  React.useEffect(() => {
    function mouseUpHandler(e) {
      dragInfoRef.current.dragInfo = null;
      setIsDragging(false);
    }

    function moveHandler(e) {
      const {
        dragInfo
      } = dragInfoRef.current;

      if (isDragging && dragInfo) {
        const deltaPx = e.clientX - dragInfo.separatorPosition;
        const containerWidthPx = refContainerWidth.current.widthPx;
        setWidthsPc(widthsPc => {
          const allowedDeltaPx = calcAllowedDelta({
            widthsPc,
            containerW: containerWidthPx,
            dragIdx: dragInfo.activePanelIdx,
            deltaPx,
            minWidth: foldedPanelWidthPx
          });
          const next = changeWidth({
            widthsPc,
            containerW: containerWidthPx,
            dragIdx: dragInfo.activePanelIdx,
            deltaPx: allowedDeltaPx,
            minWidth: foldedPanelWidthPx
          });

          if (arrEqual(next, widthsPc)) {
            return widthsPc;
          }

          dragInfo.separatorPosition = e.clientX - (deltaPx - allowedDeltaPx);
          return next;
        });
      }
    }

    if (isDragging && dragInfoRef.current.dragInfo) {
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', mouseUpHandler);
      return () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', mouseUpHandler);
      };
    }
  }, [foldedPanelWidthPx, isDragging]);
  React.useEffect(() => {
    if (!storageKey) {
      return;
    }

    if (initializedRef.current.initialized) {
      const timerId = setTimeout(() => {
        saveToStorage({
          widthsPc,
          containerW: refContainerWidth.current.widthPx,
          foldedWidthPx: foldedPanelWidthPx,
          storage,
          storageKey
        });
      }, 500);
      return () => clearTimeout(timerId);
    }

    setWidthsPc(getFromStorage({
      containerW: refContainerWidth.current.widthPx,
      foldedWidthPx: foldedPanelWidthPx,
      widthsPc,
      storage,
      storageKey
    }));
    initializedRef.current.initialized = true;
  }, [foldedPanelWidthPx, storage, storageKey, widthsPc]);
  React.useEffect(() => {
    if (!onWidthsChanged) {
      return;
    }

    const timerId = setTimeout(() => {
      const containerW = refContainerWidth.current.widthPx;
      const items = widthsPc.map((widthPc, idx) => ({
        widthPc,
        widthPx: convertPcToPx(widthPc, containerW),
        expanded: isExpanded({
          containerW,
          foldedWidthPx: foldedPanelWidthPx,
          widthPc: widthsPc[idx]
        })
      }));
      onWidthsChanged(items, containerW);
    }, onWidthChangedDelay);
    return () => {
      clearTimeout(timerId);
    };
  }, [foldedPanelWidthPx, onWidthChangedDelay, onWidthsChanged, widthsPc]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("style", {
      children: `
      ${isDragging && `
          * {
            user-select: none !important;
            cursor: col-resize !important;
          }
      `}
    `
    }), /*#__PURE__*/_jsx(AutoSizer, {
      onResize: ({
        width
      }) => onChangeContainerWidth(width),
      style: {
        height: '100%',
        display: 'flex',
        width: `100%`
      },
      children: ({
        width
      }) => React.Children.map(children, (panel, idx) =>
      /*#__PURE__*/
      // eslint-disable-next-line react/no-array-index-key
      _jsxs(PanelRoot, {
        style: {
          width: `${widthsPc[idx]}%`
        },
        children: [idx > 0 && /*#__PURE__*/_jsx(Resizer, {
          onMouseDown: e => onMouseDown(e, idx)
        }), /*#__PURE__*/_jsx(Panel, { ...panel.props,
          showExpandButton: canFoldColumn({
            containerW: width,
            widthsPc,
            foldedWidthPx: foldedPanelWidthPx
          }),
          expanded: isExpanded({
            containerW: width,
            foldedWidthPx: foldedPanelWidthPx,
            widthPc: widthsPc[idx]
          }),
          onOpen: () => onExpandColumn(idx),
          onClose: () => onFoldColumn(idx)
        })]
      }, idx))
    })]
  });
});